import { useRef, useState } from 'react';
import { Button, Card, DialogShell } from '@timely/ui';
import {
    reportIsStale, splitPlaceholders,
    type FactMap, type Observation, type ReportBasis, type Step, type StepKind,
} from '@timely/domain';
import { useActiveWorkspace } from '../workspace/WorkspaceProvider';
import { useFormatters } from '../book/useFormatters';
import { FormNotice } from '../screens/AuthLayout';
import { ApiError } from '../lib/api';
import {
    disclosureSeen, formatFact, rememberDisclosure, requestCustomerReport, useAiAvailable,
    type CustomerReportResponse,
} from './useCustomerReport';

const STEP_WORDS: Record<StepKind, string> = {
    call: 'Call',
    visit: 'Visit',
    confirm_promise: 'Confirm the promise',
    chase_cheque: 'Chase the cheque',
    review_account: 'Review the account',
    update_follow_up: 'Update the follow-up',
    escalate_internally: 'Raise internally',
    resolve_dispute: 'Resolve the dispute',
    other: 'Follow up',
};

/**
 * A validated text with its placeholders filled from the report's own facts.
 * React escapes every piece: nothing the model wrote is ever HTML.
 */
function FactText({ text, facts }: { text: string; facts: FactMap }) {
    const { money, count } = useFormatters();
    const { workspace } = useActiveWorkspace();
    return (
        <>
            {splitPlaceholders(text).map((segment, i) =>
                'fact' in segment
                    ? <strong key={i} className="num font-semibold text-label">
                        {formatFact(facts[segment.fact], { money: n => money.exact(n), count, locale: workspace.locale })}
                    </strong>
                    : <span key={i}>{segment.text}</span>)}
        </>
    );
}

function Section({ title, items, facts }: { title: string; items: Observation[]; facts: FactMap }) {
    if (items.length === 0) return null;
    return (
        <section className="mt-5">
            <h4 className="text-secondary font-semibold text-label">{title}</h4>
            <ul className="mt-2 space-y-1.5 list-disc pl-5 text-body text-label-2 leading-relaxed">
                {items.map((item, i) => <li key={i}><FactText text={item.text} facts={facts} /></li>)}
            </ul>
        </section>
    );
}

function Steps({ steps, facts }: { steps: Step[]; facts: FactMap }) {
    if (steps.length === 0) return null;
    return (
        <section className="mt-5">
            <h4 className="text-secondary font-semibold text-label">Suggested follow-up</h4>
            <ul className="mt-2 space-y-1.5 text-body text-label-2 leading-relaxed">
                {steps.map((step, i) => (
                    <li key={i}>
                        <span className="font-semibold text-label">{STEP_WORDS[step.kind] ?? 'Follow up'}:</span>{' '}
                        <FactText text={step.text} facts={facts} />
                    </li>
                ))}
            </ul>
        </section>
    );
}

/**
 * The AI customer report, on the account screen.
 *
 * **Asked for, never automatic.** Opening an account runs nothing; a report
 * costs the company money on its own key, and is written only when somebody
 * presses the button. **Beside the figures, not above them**: a small card at
 * the foot of the sidebar, and the report in a dialog, so the account's own
 * figures remain the page. The first time in a company it says plainly what
 * is sent where, that AI can be wrong, and that the figures remain the
 * authority.
 */
export function AiReportCard({ customerId, customerName, basisNow }: {
    customerId: string;
    customerName: string;
    /** What the account looks like now, to say when a report is out of date. */
    basisNow: ReportBasis;
}) {
    const { workspace, membership } = useActiveWorkspace();
    const { date } = useFormatters();
    const available = useAiAvailable(workspace.id);
    const [seen, setSeen] = useState(() => disclosureSeen(workspace.id, membership.userId));
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [result, setResult] = useState<CustomerReportResponse | null>(null);
    const [open, setOpen] = useState(false);
    const inFlight = useRef<AbortController | null>(null);

    const generate = async () => {
        // A second press while one is running does nothing: the button is
        // disabled, and the server refuses a second run for this account anyway.
        if (inFlight.current) return;
        const controller = new AbortController();
        inFlight.current = controller;
        if (!seen) {
            rememberDisclosure(workspace.id, membership.userId);
            setSeen(true);
        }
        setBusy(true);
        setError(null);
        try {
            const report = await requestCustomerReport(workspace.id, customerId, controller.signal);
            setResult(report);
            setOpen(true);
        } catch (caught) {
            if (!(caught instanceof ApiError && caught.code === 'cancelled')) {
                setError(caught instanceof ApiError ? caught.message : 'The summary could not be written. Try again.');
            }
        } finally {
            inFlight.current = null;
            setBusy(false);
        }
    };

    const cancel = () => inFlight.current?.abort();

    // Not set up for this company: nothing at all on the account (owner, 24 Oct 2026). Setting it up is Settings'
    // business, not the daily work's; somebody who may set it up finds it there.
    if (!available) return null;

    const stale = result ? reportIsStale(result.basis, basisNow) : false;
    const generatedAt = result ? `${date.short(result.generatedAt)} ${new Date(result.generatedAt)
        .toLocaleTimeString(workspace.locale, { hour: '2-digit', minute: '2-digit', timeZone: workspace.timezone })}` : '';

    return (
        <Card className="px-5 py-5" data-testid="ai-report-card">
            <h3 className="text-body font-semibold text-label mb-2">AI summary</h3>

            {!seen ? (
                <div className="text-secondary text-label-2 leading-relaxed space-y-1.5" data-testid="ai-disclosure">
                    <p>An optional summary of this account, written by AI.</p>
                    <p>
                        Selected facts about this account — its balance and ageing, follow-up, promises,
                        cheques and recent notes — are sent to your company&rsquo;s configured AI provider
                        to write it.
                    </p>
                    <p>AI can be wrong. The figures in Timely Payment remain the authority.</p>
                </div>
            ) : (
                <p className="text-secondary text-label-3 leading-relaxed">
                    Selected account facts go to your company&rsquo;s AI provider. AI can be wrong; the
                    figures here remain the authority.
                </p>
            )}

            {error && <div className="mt-3"><FormNotice tone="error">{error}</FormNotice></div>}

            <div className="mt-3 flex gap-2 flex-wrap">
                {busy ? (
                    <>
                        <Button size="sm" variant="secondary" disabled>Writing the summary…</Button>
                        <Button size="sm" variant="quiet" onClick={cancel}>Cancel</Button>
                    </>
                ) : result ? (
                    <>
                        <Button size="sm" variant="secondary" onClick={() => setOpen(true)}>View summary</Button>
                        <Button size="sm" variant="quiet" onClick={() => void generate()}>Regenerate</Button>
                    </>
                ) : (
                    <Button size="sm" variant="secondary" onClick={() => void generate()}>
                        {seen ? 'Write a summary' : 'I understand — write a summary'}
                    </Button>
                )}
            </div>
            {result && !busy && (
                <p className="mt-2 text-caption text-label-3">Generated {generatedAt}</p>
            )}

            {open && result && (
                <DialogShell
                    title="AI summary"
                    subtitle={`${customerName} · generated ${generatedAt} · ${result.providerLabel}`}
                    size="lg"
                    onClose={() => setOpen(false)}
                    footerNote={<span className="text-caption text-label-3">AI interpretation — check it against the account&rsquo;s figures.</span>}
                    footer={<>
                        <Button type="button" variant="quiet" onClick={() => void generate()} disabled={busy} busy={busy}>
                            {busy ? 'Writing…' : 'Regenerate'}
                        </Button>
                        <Button type="button" variant="primary" onClick={() => setOpen(false)} data-autofocus>Close</Button>
                    </>}
                >
                    <div data-testid="ai-report">
                        {stale && (
                            <div className="mb-4 rounded-control bg-warn-bg text-warn px-3.5 py-3 text-secondary" role="status">
                                The account has changed since this summary was written.
                            </div>
                        )}
                        <p className="text-body text-label leading-relaxed">
                            <FactText text={result.report.summary} facts={result.facts} />
                        </p>
                        <Section title="Current position" items={result.report.current_position} facts={result.facts} />
                        <Section title="What needs attention" items={result.report.needs_attention} facts={result.facts} />
                        <Section title="Positive signals" items={result.report.positive_signals} facts={result.facts} />
                        <Steps steps={result.report.suggested_next_steps} facts={result.facts} />
                        {result.report.data_limitations.length > 0 && (
                            <section className="mt-5">
                                <h4 className="text-secondary font-semibold text-label">Data limitations</h4>
                                <ul className="mt-2 space-y-1 list-disc pl-5 text-secondary text-label-3 leading-relaxed">
                                    {result.report.data_limitations.map((line, i) => (
                                        <li key={i}><FactText text={line} facts={result.facts} /></li>
                                    ))}
                                </ul>
                            </section>
                        )}
                        <p className="mt-6 text-caption text-label-3">
                            Written by {result.providerLabel} ({result.model}) from this account&rsquo;s facts as they
                            stood when you asked. The figures in the report are the ones Timely Payment holds; the
                            words around them are the AI&rsquo;s and may be wrong.
                        </p>
                    </div>
                </DialogShell>
            )}
        </Card>
    );
}
